import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Users, ArrowRight, CheckCircle2, Sparkles, GraduationCap, HeartPulse, Leaf } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';

const HowItWorks = () => {
  usePageMeta({
    title: 'How It Works | Talent Collective',
    description: 'Learn how to join the Talent Collective and start working with mission-driven organizations across India.'
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="how-it-works-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20">
        <div className="absolute top-0 left-0 right-0 h-1 bg-teal-600" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl" />
        
        <div className="container-custom relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-50 border border-teal-200/50 text-teal-700 text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4" />
            <span>Simple 4-step process</span>
          </div>
          <h1 className="heading-1 mb-4">How Talent Collective Works</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Your journey to meaningful work that creates real impact across India.
          </p>
        </div>
      </section>

      {/* For Talent Section */}
      <section className="section-padding bg-white" data-testid="talent-section">
        <div className="container-custom">
          <div className="flex items-center gap-4 mb-12">
            <div className="h-14 w-14 rounded-2xl bg-teal-100 flex items-center justify-center">
              <Users className="h-7 w-7 text-teal-600" />
            </div>
            <div>
              <h2 className="heading-2">Your Path to Impact</h2>
              <p className="text-gray-600">Find projects that match your expertise and passion.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Share Your Story',
                description: 'Create your profile and tell us about your expertise, experience, and the causes that inspire you.',
                details: ['Add your background and skills.', 'Upload your resume.', 'Share your impact areas.'],
                color: 'bg-teal-700'
              },
              {
                step: '02',
                title: 'Browse Opportunities',
                description: 'Explore projects from 64+ mission-driven organizations across education, health, environment, and women empowerment.',
                details: ['Curated impact projects.', 'Filter by your interests.', 'Discover your match.'],
                color: 'bg-teal-600'
              },
              {
                step: '03',
                title: 'Apply with Purpose',
                description: 'Submit your application with a personalized pitch explaining why their mission resonates with you.',
                details: ['Write your story.', 'Share your motivation.', 'Stand out with your pitch.'],
                color: 'bg-teal-500'
              },
              {
                step: '04',
                title: 'Create Impact Together',
                description: 'Get connected with the organization and start building solutions for India.',
                details: ['Meet the team.', 'Start making a difference.', 'Build for impact.'],
                color: 'bg-emerald-600'
              }
            ].map((item, i) => (
              <Card key={i} className="p-6 relative overflow-hidden border-2 border-gray-100 hover:border-teal-200 transition-all duration-300 rounded-2xl" data-testid={`talent-step-${i + 1}`}>
                <span className="absolute top-4 right-4 text-6xl font-extrabold text-gray-100">
                  {item.step}
                </span>
                <div className="relative">
                  <div className={`h-12 w-12 rounded-xl ${item.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <span className="text-white font-bold text-sm">{item.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{item.description}</p>
                  <ul className="space-y-2">
                    {item.details.map((detail, j) => (
                      <li key={j} className="flex items-center text-sm text-gray-500">
                        <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-500 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/signup">
              <Button className="h-14 px-10 text-base font-semibold bg-teal-700 hover:bg-teal-800 text-white rounded-full shadow-lg shadow-teal-700/25 transition-all duration-300 hover:scale-105" data-testid="talent-cta">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold mb-4">
              Where You'll Make a Difference
            </span>
            <h2 className="heading-2 mb-4">Four Impact Areas</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Join organizations working on India's most pressing challenges.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: GraduationCap, title: 'Education', desc: 'Quality learning for every child in India.', color: 'bg-sky-500', bgColor: 'bg-sky-50' },
              { icon: HeartPulse, title: 'Health', desc: 'Accessible healthcare for all communities.', color: 'bg-rose-500', bgColor: 'bg-rose-50' },
              { icon: Leaf, title: 'Environment', desc: 'A greener, cleaner tomorrow for India.', color: 'bg-emerald-500', bgColor: 'bg-emerald-50' },
              { icon: Users, title: "Women's Empowerment", desc: 'Empowering women across the workforce.', color: 'bg-violet-500', bgColor: 'bg-violet-50' }
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-white border border-gray-100 hover:shadow-lg hover:shadow-teal-100/50 transition-all duration-300">
                <div className={`h-16 w-16 rounded-2xl ${item.bgColor} flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`h-8 w-8 ${item.color.replace('bg-', 'text-')}`} />
                </div>
                <h3 className="font-bold text-gray-800 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-4">
              Got Questions?
            </span>
            <h2 className="heading-2">Common Questions</h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                q: 'What kind of projects can I work on?',
                a: "Projects span education, health, environment, and women's empowerment. Organizations need help with product management, design, engineering, and more. Whether you're building an ed-tech platform or designing a healthcare app, meaningful work awaits."
              },
              {
                q: 'What does "fractional" work mean?',
                a: 'Fractional work means part-time, project-based engagement. Instead of full-time employment, you work with organizations for a set number of hours per week. This flexibility lets you contribute to multiple causes or balance with other commitments.'
              },
              {
                q: 'Is there any cost to join?',
                a: 'No, Talent Collective is completely free for professionals. We believe in removing barriers to impact. Create your profile, browse opportunities, and apply at no cost.'
              },
              {
                q: 'How are organizations vetted?',
                a: "All organizations on the platform are part of the ACT Grants portfolio. They have received funding and support from ACT Capital Foundation and are working on verified social impact initiatives."
              }
            ].map((item, i) => (
              <Card key={i} className="p-6 bg-gray-50 border-0 rounded-2xl">
                <h3 className="font-semibold text-gray-800 mb-2">{item.q}</h3>
                <p className="text-gray-600 leading-relaxed">{item.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-teal-800 relative overflow-hidden">
        <div className="container-custom relative text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to start your impact journey?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            Join a community of professionals who are using their skills to build a better India.
          </p>
          <Link to="/signup">
            <Button className="h-14 px-10 text-base font-semibold bg-white text-teal-800 hover:bg-gray-100 rounded-full shadow-lg transition-all duration-300 hover:scale-105">
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorks;
